import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthContext';
import { Dropdown } from '../common/ui/Listbox';
import BaseCard from '../common/layout/BaseCard';
import { Button } from '../common/ui/Button'; 
import { TIMEZONES } from '../../lib/timezones';
import { captureAppException, createSupabaseError } from '../../utils/errorCaptureUtils';

export const SettingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [newEmail, setNewEmail] = useState('');
  const [timezone, setTimezone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedTimezone, setSelectedTimezone] = useState('UTC');

  // Fetch current timezone
  useEffect(() => {
    const fetchTimezone = async () => {
      const { data, error } = await supabase
        .from('users')
        .select('timezone')
        .eq('id', user?.id)
        .single();

      if (!error && data) {
        setTimezone(data.timezone);
        setSelectedTimezone(data.timezone);
        setSelectedTimezone(data.timezone);
      }
    };

    if (user?.id) {
      fetchTimezone();
    }
  }, [user?.id]);

  // Debug current timezone values
  useEffect(() => {
    console.log('Current timezone:', timezone, 'Selected:', selectedTimezone);
  }, [timezone, selectedTimezone]);

  const handleTimezoneChange = async (newTimezone: string) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Update public.users table (source of truth)
      const { error } = await supabase
        .from('users')
        .update({ timezone: newTimezone })
        .eq('id', user?.id);

      if (error) throw createSupabaseError(error, 'update_user_settings');
      
      // Update auth user metadata (for quick access without db queries)
      const { error: authError } = await supabase.auth.updateUser({
        data: { timezone: newTimezone }
      });
      
      if (authError) throw createSupabaseError(authError, 'update_auth_settings');

      setTimezone(newTimezone);
      setSelectedTimezone(newTimezone);
      setSuccess('Timezone updated successfully');
    } catch (err) {
      captureAppException(err, {
        tags: {
          context: 'settings_page',
          user_id: user?.id,
          action: 'update_timezone',
          new_timezone: newTimezone
        }
      });
      setError(err instanceof Error ? err.message : 'Failed to update timezone');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const { error } = await supabase.auth.updateUser({ email: newEmail });
      
      if (error) throw createSupabaseError(error, 'update_user_settings');
      
      setSuccess('A confirmation email has been sent to your new email address. Please check your inbox and follow the instructions to complete the change.');
      setNewEmail('');
    } catch (err) {
      captureAppException(err, {
        tags: {
          context: 'settings_page',
          user_id: user?.id,
          action: 'change_email',
          new_email: newEmail
        }
      });
      setError(err instanceof Error ? err.message : 'Failed to update email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Settings Header Card */}
      <BaseCard>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 pb-6">
          <h1 className="text-h5 font-bold text-gray-900 flex-1">Settings</h1>
        </div>
      </BaseCard>

      {/* Security Card */}
      <BaseCard>
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Security</h2>
        </div>
        <div className="p-6">
          <form onSubmit={handleEmailChange} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Current Email
              </label>
              <input
                type="email"
                value={user?.email}
                disabled
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                New Email
              </label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter new email"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {error && (
              <div className="p-4 text-sm text-red-700 bg-red-100 rounded-lg">
                {error}
              </div>
            )}

            {success && (
              <div className="p-4 text-sm text-green-700 bg-green-100 rounded-lg">
                {success}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || !newEmail}
              loading={loading}
            >
              Update Email
            </Button>
          </form>

          <div className="mt-8 pt-8 border-t border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Password Reset</h3>
            <p className="text-sm text-gray-600 mb-4">
              Need to change your password? Click below to go to our password reset page.
            </p>
            <Button
              type="button"
              onClick={() => navigate('/forgot-password')}
            >
              Reset Password
            </Button>
          </div>
        </div>
      </BaseCard>
      
      {/* Timezone Card */}
      <BaseCard overflow='visible'>
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Timezone Settings</h2>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Timezone
              </label>
              <p className="text-sm text-gray-500 mb-4">
                Select your timezone to ensure all dates and times are displayed correctly.
              </p>
              <div className="space-y-4">
                <Dropdown
                  options={TIMEZONES}
                  value={selectedTimezone}
                  onChange={(option) => setSelectedTimezone(option.id)}
                  searchable={true}
                />
                <Button
                  type="button"
                  onClick={() => handleTimezoneChange(selectedTimezone)}
                  disabled={loading || !selectedTimezone || selectedTimezone === timezone}
                  loading={loading}
                  className="mt-4"
                >
                  Save Timezone
                </Button>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                This will be used to display event times in your local timezone.
              </p>
            </div>

            {error && (
              <div className="p-4 text-sm text-red-700 bg-red-100 rounded-lg">
                {error}
              </div>
            )}

            {success && (
              <div className="p-4 text-sm text-green-700 bg-green-100 rounded-lg">
                {success}
              </div>
            )}
          </div>
        </div>
      </BaseCard>

      {/* Notifications Card */}
      <BaseCard>
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Notifications</h2>
        </div>
        <div className="p-6">
          <p className="text-gray-500 italic">Notification settings coming soon</p>
        </div>
      </BaseCard>
    </div>
  );
};